import { RiAddLine, RiLoader4Line } from '@remixicon/react';
import Money from '@shared/utils/Money';
import { useFormik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import LedgerAutocomplete from 'src/components/atoms/form/LedgerAutocomplete';
import CustomFieldsForm from 'src/components/containers/forms/CustomFieldsForm';
import { DrawerId, SideDrawer, SideDrawerHeader, useSideDrawer } from 'src/components/containers/SideDrawer';
import {
  AgAutocomplete,
  AgButton,
  AgCheckbox,
  AgInput,
  AgMoneyInput,
  AgNumericInput,
  AgSelect,
  AgText,
} from 'src/components/ui';
import { notifyUnlessSurfaced } from 'src/lib/apollo/appErrorLink';
import { useLedgerAccounts } from 'src/modules/accounting/useLedgerAccounts';
import { CommodityPicker, type CommodityPickerValue } from 'src/modules/entities/commodities';
import { countries } from 'src/utils/countries';
import AddCommodityAttributeModal from './AddCommodityAttributeModal';
import AddCommodityModal from './AddCommodityModal';
import { useCommodityAttributes } from './hooks/useCommodities';
import {
  useCreateCommodityLabel,
  useCreateCommoditySize,
  useCreateCommodityStyle,
  useCreateCommodityUnit,
  useCreateProduct,
  useUpdateProduct,
} from './product.api';

type ProductForm = {
  commodity: string;
  productCode: string;
  commodityStyle: string;
  commoditySize: string;
  commodityUnit: string;
  commodityLabel: string;
  grade: string;
  organic: boolean;
  variableWeight: boolean;
  countryOfOrigin: string;
  gtin: string;
  weightPerInventoryUnit: number | '';
  unitsPerPallet: number | '';
  costBasis: number | '';
  customFields: Record<string, string | boolean>;
  // Default accounting accounts
  receivablesAccountId: number | null;
  payablesAccountId: number | null;
  incomeAccountId: number | null;
  expenseAccountId: number | null;
};

type OpenModal = 'commodity' | 'style' | 'size' | 'unit' | 'label' | null;

type AddProductDrawerProps = { mode?: 'create' | 'edit' | 'duplicate'; productData?: any; onClose?: () => void };

export function AddProductDrawer({ mode = 'create', productData, onClose }: AddProductDrawerProps) {
  const [openModal, setOpenModal] = useState<OpenModal>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const flags = useFlags();

  const { openDrawer, closeDrawer } = useSideDrawer();
  const [pickedCommodity, setPickedCommodity] = useState<CommodityPickerValue | null>(null);
  const ledgerAccounts = useLedgerAccounts();

  // Open drawer when productData changes (for edit/duplicate)
  useEffect(() => {
    if (productData && (mode === 'edit' || mode === 'duplicate')) {
      openDrawer(DrawerId.ADD_PRODUCT);
    }
  }, [productData, mode, openDrawer]);

  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const createCommodityStyle = useCreateCommodityStyle();
  const createCommoditySize = useCreateCommoditySize();
  const createCommodityUnit = useCreateCommodityUnit();
  const createCommodityLabel = useCreateCommodityLabel();

  const getInitialValues = (): ProductForm => {
    if (mode !== 'create' && productData) {
      return {
        commodity: String(productData.commodity?.id || ''),
        productCode: productData.productCode || '',
        commodityStyle: String(productData.commodityStyle?.id || ''),
        commoditySize: String(productData.commoditySize?.id || ''),
        commodityUnit: String(productData.commodityUnit?.id || ''),
        commodityLabel: String(productData.commodityLabel?.id || ''),
        grade: productData.grade || '',
        organic: productData.organic || false,
        variableWeight: productData.variableWeight || false,
        countryOfOrigin: productData.countryOfOrigin || '',
        gtin: productData.gtinCode || '',
        weightPerInventoryUnit: productData.weightPerInventoryUnit ?? '',
        unitsPerPallet: productData.cartonsPerPallet ?? '',
        costBasis: productData.costBasis ? Money.toDollars(productData.costBasis) : '',
        customFields: productData.customFields || {},
        receivablesAccountId: productData.receivablesAccountId || null,
        payablesAccountId: productData.payablesAccountId || null,
        incomeAccountId: productData.incomeAccountId || null,
        expenseAccountId: productData.expenseAccountId || null,
      };
    }
    return {
      commodity: '',
      productCode: '',
      commodityStyle: '',
      commoditySize: '',
      commodityUnit: '',
      commodityLabel: '',
      grade: '',
      organic: false,
      variableWeight: false,
      countryOfOrigin: '',
      gtin: '',
      weightPerInventoryUnit: '',
      unitsPerPallet: 1,
      costBasis: '',
      customFields: {},
      receivablesAccountId: null,
      payablesAccountId: null,
      incomeAccountId: null,
      expenseAccountId: null,
    };
  };

  const f = useFormik<ProductForm>({
    initialValues: getInitialValues(),
    enableReinitialize: true,
    validate: (values) => {
      const errors: Partial<Record<keyof ProductForm, string>> = {};

      if (!values.commodity) {
        errors.commodity = 'Commodity is required';
      }

      if (!values.commodityUnit) {
        errors.commodityUnit = 'Unit of Measure is required';
      }

      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      try {
        const input = {
          commodityId: undefined,
          productCode: values.productCode || null,
          commodityStyleId: values.commodityStyle ? Number(values.commodityStyle) : null,
          commoditySizeId: values.commoditySize ? Number(values.commoditySize) : null,
          commodityUnitId: values.commodityUnit ? Number(values.commodityUnit) : null,
          commodityLabelId: values.commodityLabel ? Number(values.commodityLabel) : null,
          grade: values.grade || null,
          organic: values.organic,
          variableWeight: values.variableWeight,
          weightPerInventoryUnit: values.weightPerInventoryUnit === '' ? 0 : Number(values.weightPerInventoryUnit),
          countryOfOrigin: values.countryOfOrigin || null,
          gtinCode: values.gtin || null,
          cartonsPerPallet: values.unitsPerPallet ? Number(values.unitsPerPallet) : null,
          costBasis: values.costBasis ? Money.toCents(values.costBasis) : null,
          customFields: Object.keys(values.customFields).length > 0 ? values.customFields : null,
          receivablesAccountId: values.receivablesAccountId,
          payablesAccountId: values.payablesAccountId,
          incomeAccountId: values.incomeAccountId,
          expenseAccountId: values.expenseAccountId,
        };

        if (mode === 'edit' && productData?.id) {
          // Update existing product
          await updateProduct(productData.id, input);
        } else {
          // Create new product (for both 'create' and 'duplicate' modes)
          input.commodityId = Number(values.commodity);
          await createProduct(input);
        }

        resetForm();
        closeDrawer();
        onClose?.();
      } catch (error) {
        console.error(`Error ${mode === 'edit' ? 'updating' : 'creating'} product:`, error);
        // A typed server error (e.g. another product already has these specs) or a network error is already on screen.
        notifyUnlessSurfaced(error, `Error ${mode === 'edit' ? 'updating' : 'creating'} product.`);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const commodityId = f.values.commodity ? Number(f.values.commodity) : null;
  const { data, refetch } = useCommodityAttributes(commodityId);
  const selectedCommodity = data?.commodity ?? undefined;
  // The picked commodity, else the product's own, else the attributes read: its name never goes blank.
  const commodityValue = useMemo((): CommodityPickerValue | null => {
    if (commodityId === null) return null;
    if (pickedCommodity?.id === commodityId) return pickedCommodity;
    const known = productData?.commodity?.id === commodityId ? productData.commodity : selectedCommodity;
    return known ? { id: commodityId, name: known.name } : null;
  }, [commodityId, pickedCommodity, productData, selectedCommodity]);

  // Handlers for adding new attributes
  const handleAddStyle = async (commodityId: number, name: string) => {
    const result = await createCommodityStyle({ commodityId, name });
    const newStyleId = result.data?.createCommodityStyle?.id;
    if (!newStyleId) {
      throw new Error('The server did not return the new variety.');
    }
    await refetch();
    f.setFieldValue('commodityStyle', String(newStyleId));
    return { id: newStyleId, name };
  };

  const handleAddSize = async (commodityId: number, name: string) => {
    const result = await createCommoditySize({ commodityId, name });
    const newSizeId = result.data?.createCommoditySize?.id;
    if (!newSizeId) {
      throw new Error('The server did not return the new pack style.');
    }
    await refetch();
    f.setFieldValue('commoditySize', String(newSizeId));
    return { id: newSizeId, name };
  };

  const handleAddUnit = async (commodityId: number, name: string) => {
    const result = await createCommodityUnit({ commodityId, name });
    const newUnitId = result.data?.createCommodityUnit?.id;
    if (!newUnitId) {
      throw new Error('The server did not return the new unit.');
    }
    await refetch();
    f.setFieldValue('commodityUnit', String(newUnitId));
    return { id: newUnitId, name };
  };

  const handleAddLabel = async (commodityId: number, name: string) => {
    const result = await createCommodityLabel({ commodityId, name });
    const newLabelId = result.data?.createCommodityLabel?.id;
    if (!newLabelId) {
      throw new Error('The server did not return the new label.');
    }
    await refetch();
    f.setFieldValue('commodityLabel', String(newLabelId));
    return { id: newLabelId, name };
  };

  const drawerTitle = mode === 'edit' ? 'Edit Product' : mode === 'duplicate' ? 'Duplicate Product' : 'Add Product';
  const submitButtonText = mode === 'edit' ? 'Update' : 'Add';

  const handleUpdateCustomFields = useCallback(
    (field: { key: string }, value: string | boolean) => {
      const fieldValues = f.values.customFields ?? {};
      f.setFieldValue('customFields', { ...fieldValues, [field.key]: value });
    },
    [f]
  );

  const styleOptions = useMemo(
    () => [
      { value: '', label: '--' },
      ...(selectedCommodity?.styles
        ?.filter((item) => item.name.trim().length > 0)
        .map(({ name, id }) => ({ value: String(id), label: name })) ?? []),
    ],
    [selectedCommodity]
  );

  const sizeOptions = useMemo(
    () => [
      { value: '', label: '--' },
      ...(selectedCommodity?.sizes
        ?.filter((item) => item.name.trim().length > 0)
        .map(({ name, id }) => ({ value: String(id), label: name })) ?? []),
    ],
    [selectedCommodity]
  );

  const unitOptions = useMemo(
    () => [
      { value: '', label: '--' },
      ...(selectedCommodity?.units
        ?.filter((item) => item.name.trim().length > 0)
        .map(({ name, id }) => ({ value: String(id), label: name })) ?? []),
    ],
    [selectedCommodity]
  );

  const labelOptions = useMemo(
    () => [
      { value: '', label: '--' },
      ...(selectedCommodity?.labels
        ?.filter((item) => item.name.trim().length > 0)
        .map(({ name, id }) => ({ value: String(id), label: name })) ?? []),
    ],
    [selectedCommodity]
  );

  return (
    <>
      <AddCommodityModal handleClose={() => setOpenModal(null)} openAddCommodity={openModal === 'commodity'} />
      {f.values.commodity && (
        <>
          <AddCommodityAttributeModal
            open={openModal === 'style'}
            handleClose={() => setOpenModal(null)}
            attributeName="Variety"
            commodityId={Number(f.values.commodity)}
            onAdd={handleAddStyle}
          />
          <AddCommodityAttributeModal
            open={openModal === 'size'}
            handleClose={() => setOpenModal(null)}
            attributeName="Pack Style"
            commodityId={Number(f.values.commodity)}
            onAdd={handleAddSize}
          />
          <AddCommodityAttributeModal
            open={openModal === 'unit'}
            handleClose={() => setOpenModal(null)}
            attributeName="Unit"
            commodityId={Number(f.values.commodity)}
            onAdd={handleAddUnit}
          />
          <AddCommodityAttributeModal
            open={openModal === 'label'}
            handleClose={() => setOpenModal(null)}
            attributeName="Label"
            commodityId={Number(f.values.commodity)}
            onAdd={handleAddLabel}
          />
        </>
      )}
      {mode === 'create' && (
        <AgButton variant="primary" onPress={() => openDrawer(DrawerId.ADD_PRODUCT)}>
          <RiAddLine size={16} />
          Add Product
        </AgButton>
      )}
      <SideDrawer drawerId={DrawerId.ADD_PRODUCT} onClose={onClose} paperSx={{ p: 0 }}>
        <div className="pt-16 px-24">
          <SideDrawerHeader title={drawerTitle} onClose={onClose} />
        </div>
        <form onSubmit={f.handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex flex-col gap-6 min-w-125 p-24 flex-1 overflow-y-auto">
            <CommodityPicker
              label="Commodity"
              isRequired
              value={commodityValue}
              onChange={(value) => {
                if (!value) return;
                setPickedCommodity(value);
                f.setFieldValue('commodity', String(value.id));
              }}
              isClearable={false}
              isDisabled={mode === 'edit'}
              isInvalid={f.touched.commodity && !!f.errors.commodity}
              placeholder="Select commodity"
              footer={
                <AgButton variant="ghost" onPress={() => setOpenModal('commodity')} className="w-full justify-start">
                  <RiAddLine size={20} />
                  Add New Commodity
                </AgButton>
              }
            />

            <AgInput
              autoFocus
              label="Product Code"
              value={f.values.productCode}
              onChange={(value) => f.setFieldValue('productCode', value)}
            />

            <div className="grid grid-cols-2 gap-4">
              <AgSelect
                label="Variety"
                value={f.values.commodityStyle}
                onChange={(key) => f.setFieldValue('commodityStyle', String(key))}
                options={styleOptions}
                isDisabled={!f.values.commodity}
                footer={
                  <AgButton
                    variant="ghost"
                    onPress={() => setOpenModal('style')}
                    isDisabled={!f.values.commodity}
                    className="w-full justify-start"
                  >
                    <RiAddLine size={20} />
                    Add New Variety
                  </AgButton>
                }
              />
              <AgSelect
                label="Pack Style"
                value={f.values.commoditySize}
                onChange={(key) => f.setFieldValue('commoditySize', String(key))}
                options={sizeOptions}
                isDisabled={!f.values.commodity}
                footer={
                  <AgButton
                    variant="ghost"
                    onPress={() => setOpenModal('size')}
                    isDisabled={!f.values.commodity}
                    className="w-full justify-start"
                  >
                    <RiAddLine size={20} />
                    Add New Pack Style
                  </AgButton>
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <AgSelect
                label="Unit of Measure"
                isRequired
                value={f.values.commodityUnit}
                onChange={(key) => f.setFieldValue('commodityUnit', String(key))}
                options={unitOptions}
                isDisabled={!f.values.commodity}
                isInvalid={f.touched.commodityUnit && !!f.errors.commodityUnit}
                footer={
                  <AgButton
                    variant="ghost"
                    onPress={() => setOpenModal('unit')}
                    isDisabled={!f.values.commodity}
                    className="w-full justify-start"
                  >
                    <RiAddLine size={20} />
                    Add New Unit
                  </AgButton>
                }
              />
              <AgSelect
                label="Label"
                value={f.values.commodityLabel}
                onChange={(key) => f.setFieldValue('commodityLabel', String(key))}
                options={labelOptions}
                isDisabled={!f.values.commodity}
                footer={
                  <AgButton
                    variant="ghost"
                    onPress={() => setOpenModal('label')}
                    isDisabled={!f.values.commodity}
                    className="w-full justify-start"
                  >
                    <RiAddLine size={20} />
                    Add New Label
                  </AgButton>
                }
              />
            </div>

            <div className="grid grid-cols-2">
              <AgCheckbox
                isSelected={f.values.organic}
                onChange={(isSelected) => f.setFieldValue('organic', isSelected)}
                label="Organic"
              />
              <AgCheckbox
                isSelected={f.values.variableWeight}
                onChange={(isSelected) => f.setFieldValue('variableWeight', isSelected)}
                label="Variable Weight"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <AgNumericInput
                label="Weight Per Inventory Unit"
                value={f.values.weightPerInventoryUnit === '' ? null : f.values.weightPerInventoryUnit}
                onChange={(value) => f.setFieldValue('weightPerInventoryUnit', value ?? '')}
                decimalScale={2}
              />
              <AgNumericInput
                label="Units Per Pallet"
                value={f.values.unitsPerPallet === '' ? null : f.values.unitsPerPallet}
                onChange={(value) => f.setFieldValue('unitsPerPallet', value ?? '')}
                decimalScale={0}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <AgInput label="Grade" value={f.values.grade} onChange={(value) => f.setFieldValue('grade', value)} />
              <AgAutocomplete
                label="Country of Origin"
                options={countries}
                getOptionLabel={(option) => (option.code ? `${option.name} (${option.code})` : '')}
                getOptionKey={(option) => option.code || String(option.id)}
                value={countries.find((c) => c.code === f.values.countryOfOrigin) ?? null}
                onChange={(value) => f.setFieldValue('countryOfOrigin', value?.code ?? '')}
                isClearable
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <AgInput label="GTIN" value={f.values.gtin} onChange={(value) => f.setFieldValue('gtin', value)} />
              <AgMoneyInput
                label="Cost Basis"
                value={f.values.costBasis != null ? Math.round(f.values.costBasis * 100) : null}
                onChange={(cents) => f.setFieldValue('costBasis', cents != null ? cents / 100 : '')}
              />
            </div>

            <CustomFieldsForm
              customFieldValues={f.values.customFields ?? {}}
              customFieldsKey="product"
              handleUpdateCustomFields={handleUpdateCustomFields}
            />

            {flags.accounting && (
              <>
                <div className="border-t border-neutral-5" />
                <AgText size="16" weight="semibold">
                  Default Accounts
                </AgText>
                <AgText size="14" color="secondary" className="mb-4">
                  Override organization-level default accounts for this product
                </AgText>
                <div className="grid grid-cols-2 gap-4">
                  <LedgerAutocomplete
                    value={ledgerAccounts.find((a) => a.id === f.values.receivablesAccountId) ?? null}
                    onChange={(_, value) => f.setFieldValue('receivablesAccountId', value?.id ?? null)}
                    options={ledgerAccounts}
                    label="Accounts Receivable"
                    accountTypes={['CurrentAssets']}
                    textFieldProps={{ required: false }}
                  />
                  <LedgerAutocomplete
                    value={ledgerAccounts.find((a) => a.id === f.values.payablesAccountId) ?? null}
                    onChange={(_, value) => f.setFieldValue('payablesAccountId', value?.id ?? null)}
                    options={ledgerAccounts}
                    label="Accounts Payable"
                    accountTypes={['CurrentLiabilities']}
                    textFieldProps={{ required: false }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <LedgerAutocomplete
                    value={ledgerAccounts.find((a) => a.id === f.values.incomeAccountId) ?? null}
                    onChange={(_, value) => f.setFieldValue('incomeAccountId', value?.id ?? null)}
                    options={ledgerAccounts}
                    label="Income Account"
                    accountTypes={['Income', 'OtherIncome']}
                    textFieldProps={{ required: false }}
                  />
                  <LedgerAutocomplete
                    value={ledgerAccounts.find((a) => a.id === f.values.expenseAccountId) ?? null}
                    onChange={(_, value) => f.setFieldValue('expenseAccountId', value?.id ?? null)}
                    options={ledgerAccounts}
                    label="Expense Account"
                    accountTypes={['DirectCosts', 'IndirectCosts', 'OtherCosts']}
                    textFieldProps={{ required: false }}
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex gap-2 p-24 justify-end border-t border-neutral-5">
            <AgButton
              variant="white"
              onPress={() => {
                f.resetForm();
                closeDrawer();
                onClose?.();
              }}
              isDisabled={isSubmitting}
            >
              Cancel
            </AgButton>
            <AgButton type="submit" variant="primary" isDisabled={isSubmitting}>
              {isSubmitting ? <RiLoader4Line size={20} className="animate-spin" /> : null}
              {isSubmitting ? `${mode === 'edit' ? 'Updating' : 'Adding'}...` : submitButtonText}
            </AgButton>
          </div>
        </form>
      </SideDrawer>
    </>
  );
}
